/** Vencimentos do cliente: procuração e-CAC, certificado digital, alvará, licenças, contratos — com alerta e tarefa de renovação. */
import { z } from 'zod';
import { DataISO, Id } from './comum.js';

export const TipoVencimento = z.enum(['PROCURACAO_ECAC', 'CERTIFICADO_DIGITAL', 'ALVARA', 'LICENCA', 'CONTRATO', 'OUTRO']);
export type TipoVencimento = z.infer<typeof TipoVencimento>;
export const SituacaoVencimento = z.enum(['VENCIDO', 'A_VENCER', 'OK']);
export type SituacaoVencimento = z.infer<typeof SituacaoVencimento>;

export const VencimentoBody = z.object({
  /** nulo = vencimento do próprio escritório */
  clienteId: Id.nullable().default(null),
  tipo: TipoVencimento,
  descricao: z.string().trim().max(160).nullable().default(null),
  validade: DataISO,
  /** a tarefa de renovação nasce tantos dias antes da validade */
  avisarDiasAntes: z.number().int().min(0).max(365).default(30),
  observacao: z.string().trim().max(500).nullable().default(null),
});
export type VencimentoBody = z.infer<typeof VencimentoBody>;
/** Edição: só o que vier no corpo é alterado (sem defaults). Mudar a validade = renovado: a próxima tarefa nasce de novo na janela. */
export const VencimentoEditarBody = z.object({
  tipo: TipoVencimento.optional(), descricao: z.string().trim().max(160).nullable().optional(), validade: DataISO.optional(),
  avisarDiasAntes: z.number().int().min(0).max(365).optional(), observacao: z.string().trim().max(500).nullable().optional(),
});
export type VencimentoEditarBody = z.infer<typeof VencimentoEditarBody>;

export const Vencimento = z.object({
  id: Id, clienteId: Id.nullable(), clienteNome: z.string().nullable(), tipo: TipoVencimento, rotulo: z.string(), descricao: z.string().nullable(), validade: DataISO, avisarDiasAntes: z.number().int(),
  /** procuração consultada no e-CAC e não encontrada */
  /** CERT/SENHA = acompanha um item do cofre (a validade vem de lá) */
  ausente: z.boolean(), origem: z.enum(['MANUAL', 'INTEGRA', 'CERT', 'SENHA', 'ECNPJ']), observacao: z.string().nullable(), consultadoEm: z.string().nullable(),
  /** dias até a validade (negativo = já venceu) */
  dias: z.number().int(), situacao: SituacaoVencimento,
  tarefaId: Id.nullable(), tarefaStatus: z.string().nullable(),
});
export type Vencimento = z.infer<typeof Vencimento>;

export const VencimentosQuery = z.object({ clienteId: Id.optional(), tipo: TipoVencimento.optional(), situacao: SituacaoVencimento.optional() });
export const VencimentosResposta = z.object({ itens: z.array(Vencimento), resumo: z.object({ vencidos: z.number().int(), aVencer: z.number().int(), ok: z.number().int() }) });
export type VencimentosResposta = z.infer<typeof VencimentosResposta>;
/** Clientes (PJ ativos) que dá para consultar a procuração no e-CAC; cada consulta é uma chamada cobrada. */
export const ProcuracoesAlvo = z.object({ clientes: z.array(z.object({ clienteId: Id, clienteNome: z.string(), documento: z.string(), validade: DataISO.nullable(), ausente: z.boolean(), consultadoEm: z.string().nullable() })) });
export type ProcuracoesAlvo = z.infer<typeof ProcuracoesAlvo>;
export const VencimentoTarefasGeradas = z.object({ criadas: z.number().int() });
